/**
 * BaseWidget - Базовый компонент для всех виджетов
 * =================================================
 * 
 * Предоставляет общую функциональность для виджетов
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertDescription } from '../ui/alert.jsx';
import { Button } from '../ui/button';
import { RefreshCw, AlertCircle } from 'lucide-react';

/**
 * Компонент загрузки
 */
export function WidgetSkeleton({ rows = 3 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-4 w-full" />
      ))}
    </div>
  );
}

/**
 * Компонент ошибки
 */
export function WidgetError({ error, onRetry }) {
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between">
        <span>{error || 'Failed to load data'}</span>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="ml-2"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Компонент пустого состояния
 */
export function WidgetEmpty({ message = 'No data available' }) {
  return (
    <div className="text-center py-8 text-muted-foreground">
      <p>{message}</p>
    </div>
  );
}

/**
 * Базовый виджет
 */
export function BaseWidget({
  title,
  subtitle,
  loading,
  error,
  data,
  onRetry,
  children,
  className = '',
  headerAction,
  showRefresh = true,
  emptyMessage = 'No data available',
  skeletonRows = 3,
  toolbar = null
}) {
  // Рендерим содержимое в зависимости от состояния
  const renderContent = () => {
    if (loading) {
      return <WidgetSkeleton rows={skeletonRows} />;
    }
    
    if (error) {
      return <WidgetError error={error} onRetry={onRetry} />;
    }
    
    if (!data || (Array.isArray(data) && data.length === 0)) {
      return <WidgetEmpty message={emptyMessage} />;
    }
    
    return children;
  };
  
  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base font-medium">
            {title}
          </CardTitle>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1">
              {subtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {headerAction}
          {showRefresh && onRetry && !loading && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onRetry}
              className="h-8 w-8"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Фильтры виджета живут и во время загрузки, и при ошибке: иначе из ошибки не выбраться */}
        {toolbar && <div className="mb-4">{toolbar}</div>}
        {renderContent()}
      </CardContent>
    </Card>
  );
}

/**
 * HOC для добавления базовой функциональности к виджету
 */
export function withBaseWidget(Component, defaultProps = {}) {
  return function WrappedWidget(props) {
    const mergedProps = { ...defaultProps, ...props };
    
    return (
      <BaseWidget {...mergedProps}>
        <Component {...mergedProps} />
      </BaseWidget>
    );
  };
}

/**
 * Хук для использования с BaseWidget
 */
export function useBaseWidget(widgetData) {
  const { data, loading, error, refetch } = widgetData;
  
  return {
    data,
    loading,
    error,
    onRetry: refetch,
    // Дополнительные утилиты
    isEmpty: !data || (Array.isArray(data) && data.length === 0),
    hasData: data && (!Array.isArray(data) || data.length > 0),
    isReady: !loading && !error
  };
}

export default BaseWidget;
